import { Editor } from '@tiptap/core';
import { Link } from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, JSONContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import cx from 'classnames';
import { FOOTNOTE_NAME } from 'components/learning-center/assignments/slides/blocks/RichTextBlock/extensions';
import { Link03, LinkBroken02, MinusCircle } from 'ethena-icons';
import { useToggle } from 'lib/hooks';
import _ from 'lodash';
import { useState } from 'react';
import { useRichTextEditorContext } from './context';
import css from './FootnoteList.module.scss';
import { LinkModal } from './LinkModal';

export interface IFootnoteListProps {
  className?: string;
}

export const FootnoteList = ({ className }: IFootnoteListProps) => {
  const { editor } = useRichTextEditorContext();
  const [selectedFootnoteId, setSelectedFootnoteId] = useState<string>(null);
  const [isLinkModalVisible, toggleLinkModal] = useToggle(false);
  const [link, setLink] = useState<string>('');
  const [selectedEditor, setSelectedEditor] = useState<Editor>(null);

  const getFootnotes = (): JSONContent[] => {
    if (!editor) return [];

    const extractedFootnotes: JSONContent[] = [];
    editor.state.doc.descendants((node) => {
      if (node.type.name === FOOTNOTE_NAME) {
        extractedFootnotes.push(node.toJSON());
      }
    });
    return extractedFootnotes;
  };

  const footnotes = getFootnotes();

  const handleSetLink = (href: string) => {
    if (href && selectedEditor) {
      selectedEditor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href })
        .run();
    }
    toggleLinkModal();
  };

  const handleLinkClick = () => {
    if (selectedEditor) {
      setLink(selectedEditor.getAttributes('link').href);
      toggleLinkModal();
    }
  };

  const handleUnlinkClick = () => {
    if (selectedEditor) {
      selectedEditor.chain().focus().unsetLink().run();
    }
  };

  const isLinkActive = selectedEditor ? selectedEditor.isActive('link') : false;

  const onFootnoteFocus = (footnoteId: string, footnoteEditor: Editor) => {
    setSelectedFootnoteId(footnoteId);
    setSelectedEditor(footnoteEditor);
  };

  if (_.isEmpty(footnotes)) {
    return null;
  }

  return (
    <div className={cx(css.footnotesContainer, className)}>
      <div className={css.footnotesHeader}>
        <p className={css.footnotesHeading}>Footnotes</p>
        <div className={css.footnoteControls}>
          <button
            className={cx(css.menuButton, css.footnoteButton)}
            aria-disabled={!selectedFootnoteId}
            onClick={handleLinkClick}
            title="Add link"
          >
            <Link03 height={12} width={12} />
          </button>
          <button
            className={cx(css.menuButton, css.footnoteButton)}
            aria-disabled={!selectedFootnoteId || !isLinkActive}
            onClick={handleUnlinkClick}
            title="Remove link"
          >
            <LinkBroken02 height={12} width={12} />
          </button>
        </div>
      </div>
      <ol className={css.footnotesList}>
        {_.map(footnotes, (footnote, index) => {
          return (
            <FootnoteEditor
              key={footnote.attrs?.id}
              footnote={footnote}
              footnoteNumber={index + 1}
              onUpdate={(footnoteId, newContent) =>
                editor.commands.updateFootnote(footnoteId, newContent)
              }
              onRemove={(footnoteId) => {
                editor.commands.deleteFootnote(footnoteId);
              }}
              onFocus={onFootnoteFocus}
            />
          );
        })}
      </ol>
      <LinkModal
        isVisible={isLinkModalVisible}
        onClose={toggleLinkModal}
        onConfirm={handleSetLink}
        value={link}
      />
    </div>
  );
};

interface IFootnoteEditorProps {
  footnote: JSONContent;
  footnoteNumber: number;
  onUpdate: (footnoteId: string, newContent: JSONContent) => void;
  onRemove: (footnoteId: string) => void;
  onFocus?: (footnoteId: string, editor: Editor) => void;
}

const FootnoteEditor = ({
  footnote,
  footnoteNumber,
  onUpdate,
  onRemove,
  onFocus,
}: IFootnoteEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: false,
      }),
      Placeholder.configure({
        placeholder: 'Start typing...',
      }),
      Link.configure({
        openOnClick: false,
      }),
    ],
    onCreate: ({ editor }) => {
      if (footnote.content && footnote.content.length > 0) {
        editor.commands.setContent({ type: 'doc', content: footnote.content });
      }
    },
    onUpdate: ({ editor }) => {
      const content = editor.getJSON();
      onUpdate(footnote.attrs?.id, content);
    },
    onFocus: () => {
      if (onFocus && footnote.attrs?.id) {
        onFocus(footnote.attrs.id, editor);
      }
    },
  });

  if (!editor) {
    return null;
  }

  return (
    <li className={css.footnoteItem}>
      <span className={css.footnoteNumber}>{footnoteNumber}.</span>
      <EditorContent className={css.footnoteEditor} editor={editor} />
      <button
        className={css.removeFootnoteButton}
        onClick={() => onRemove(footnote.attrs?.id)}
      >
        <MinusCircle height={16} width={16} />
      </button>
    </li>
  );
};
